// Facebook Authentication Utility for React App

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
    checkLoginState?: () => void;
  }
}

export interface FacebookUser {
  id: string;
  name: string;
  email: string;
  picture?: {
    data: {
      url: string;
    };
  };
}

export interface FacebookAuthResponse {
  status: 'connected' | 'not_authorized' | 'unknown';
  authResponse?: {
    accessToken: string;
    expiresIn: string;
    signedRequest: string;
    userID: string;
  };
}

export class FacebookAuth {
  private static isInitialized = false;

  // Initialize Facebook SDK
  static async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isInitialized) {
        resolve();
        return;
      }

      // Check if FB is already loaded
      if (window.FB) {
        this.isInitialized = true;
        resolve();
        return;
      }

      // Wait for FB to load
      const checkFB = () => {
        if (window.FB) {
          this.isInitialized = true;
          resolve();
        } else {
          setTimeout(checkFB, 100);
        }
      };

      // Start checking after a short delay
      setTimeout(checkFB, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('Facebook SDK failed to load'));
      }, 10000);
    });
  }

  // Login with Facebook
  static async login(): Promise<{ accessToken: string; user: FacebookUser }> {
    await this.initialize();

    return new Promise((resolve, reject) => {
      window.FB.login((response: FacebookAuthResponse) => {
        this.statusChangeCallback(response, resolve, reject);
      }, { scope: 'public_profile,email' });
    });
  }

  // Status change callback handler
  private static statusChangeCallback(
    response: FacebookAuthResponse,
    resolve: (value: { accessToken: string; user: FacebookUser }) => void,
    reject: (reason?: any) => void
  ) {
    if (response.status === 'connected' && response.authResponse) {
      // Get user info
      window.FB.api('/me', { fields: 'id,name,email,picture' }, (userInfo: FacebookUser) => {
        if (userInfo) {
          resolve({
            accessToken: response.authResponse!.accessToken,
            user: userInfo
          });
        } else {
          reject(new Error('Failed to get user information'));
        }
      });
    } else if (response.status === 'not_authorized') {
      reject(new Error('User not authorized'));
    } else {
      reject(new Error('Facebook login failed or was cancelled'));
    }
  }

  // Logout from Facebook
  static async logout(): Promise<void> {
    await this.initialize();

    return new Promise((resolve) => {
      window.FB.logout(() => {
        resolve();
      });
    });
  }

  // Check login status
  static async getLoginStatus(): Promise<FacebookAuthResponse> {
    await this.initialize();

    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: FacebookAuthResponse) => {
        resolve(response);
      });
    });
  }

  // Get current user info
  static async getCurrentUser(): Promise<FacebookUser | null> {
    const loginStatus = await this.getLoginStatus();
    
    if (loginStatus.status === 'connected') {
      return new Promise((resolve, reject) => {
        window.FB.api('/me', { fields: 'id,name,email,picture' }, (userInfo: FacebookUser) => {
          if (userInfo) {
            resolve(userInfo);
          } else {
            reject(new Error('Failed to get user info'));
          }
        });
      });
    }
    
    return null;
  }

  // Check login state (similar to Facebook's checkLoginState function)
  static async checkLoginState(): Promise<FacebookAuthResponse> {
    await this.initialize();
    
    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: FacebookAuthResponse) => {
        resolve(response);
      });
    });
  }

  // Make checkLoginState available globally for Facebook login button
  static setupGlobalCallback() {
    (window as any).checkLoginState = async () => {
      const response = await this.checkLoginState();
      // You can add custom logic here for handling the response
      console.log('Facebook login state:', response);
      return response;
    };
  }
}
